import { v4 as uuid } from 'uuid';
import { createInfiniteQuery } from '@tanstack/svelte-query';
import { openDB, type IDBPDatabase } from 'idb';
import { range } from '~/board/utils';
import type {
  Board,
  Coord,
  Highlighted,
  Intersections,
  Match,
  Puzzle,
  Tile,
  Turn,
  WordSort,
} from '~/types';

export type GameMode = 'daily' | 'mini' | 'classic' | 'twist';

export type DailyVariant = '5x5' | '6x6';

export type TwistVariant = '6x6';

export type GameDB<T extends {}> = {
  words: {
    key: string;
    value: Match;
  };
  turns: {
    key: string;
    value: Turn;
  };
  game: {
    key: string;
    value: Game<T>;
  };
  indexes: Record<WordSort, number>;
};

export class Game<T extends {} = Tile> {
  db?: IDBPDatabase<GameDB<T>>;
  static gameKey = 'game';
  static wordKey = 'words';
  static turnKey = 'turns';
  static _mode: GameMode;

  id: string;
  dims: Coord = [6, 7];

  prevWordPointer = 0;
  prevTurnPointer = -1; // turns are incremented after words, so always behind by 1
  lastTurnTimestamp = Date.now();

  startedAt: number;
  endedAt?: number;
  duration?: number;

  words = $state(0);
  turns = $state(0);

  disabled = $state(false);
  board = $state<Board<T>>([]);
  finalBoard?: Board<T>;
  latestWord = $state<Match>();

  swaps = $state(5);
  shuffles = $state(1);
  score = $state(0);
  lost = $state(false);

  marquee = $state<string>();
  highlighted = $state<Highlighted>({});
  intersections = $state<Intersections>({});

  constructor() {
    this.id = uuid();
    this.startedAt = Date.now();
  }

  get mode(): GameMode {
    return (this.constructor as typeof Game)._mode;
  }

  set mode(mode: string) {
    // noop
  }

  static upgradeBase(db: IDBPDatabase<any>) {
    const { wordKey, turnKey, gameKey } = Game;
    db.createObjectStore(gameKey, { keyPath: 'id' });
    const wordStore = db.createObjectStore(wordKey, { keyPath: 'index' });
    wordStore.createIndex('index', 'index');
    wordStore.createIndex('score', 'score');
    wordStore.createIndex('length', 'length');
    wordStore.createIndex('alpha', 'alpha');
    const turnStore = db.createObjectStore(turnKey, { keyPath: 'index' });
    turnStore.createIndex('index', 'index');
  }

  async init(suffix: string = '') {
    const { wordKey, turnKey } = Game;
    this.db = await openDB<GameDB<T>>(this.mode + suffix, 1, {
      upgrade(db) {
        Game.upgradeBase(db);
      },
    });
    this.words = await this.db.count(wordKey);
    this.turns = await this.db.count(turnKey);
    return this;
  }

  serialize() {
    return {
      id: this.id,
      mode: this.mode,
      startedAt: this.startedAt,
      endedAt: this.endedAt,
      board: $state.snapshot(this.board),
      finalBoard: this.finalBoard,
      disabled: this.disabled,
      latestWord: $state.snapshot(this.latestWord),
      swaps: this.swaps,
      shuffles: this.shuffles,
      score: this.score,
      lost: this.lost,
      words: this.words,
      turns: this.turns,
      prevTurnPointer: this.prevTurnPointer,
      prevWordPointer: this.prevWordPointer,
      lastTurnTimestamp: this.lastTurnTimestamp,
    };
  }

  async save() {
    if (!this.db) throw Error('not initialized');
    await this.db.put(Game.gameKey, this.serialize());
  }

  static async load(puzzle?: Puzzle) {
    const game = await new this().init();
    if (!game.db) throw Error('not initialized');
    if (!puzzle) {
      const [record] = await game.db.getAll(Game.gameKey);
      Object.assign(game, record);
      // Re-sync from IDB counts — serialized words/turns can lag if the app
      // crashed after appendWord/appendTurn but before save().
      game.words = await game.db.count(Game.wordKey);
      game.turns = await game.db.count(Game.turnKey);
    } else {
      // TODO
      // initialize startedAt?
      // Destructure words/turns before Object.assign to prevent Svelte's $state from
      // wrapping the arrays and making this.words/this.turns a Proxy (which IDB can't clone).
      const { words, turns, ...restPuzzle } = puzzle;
      Object.assign(game, restPuzzle);
      // Clear any existing local data before restoring so old words/turns don't
      // corrupt indices and cause prevWordPointer > words.length in appendTurn.
      await game.clear();
      await game.bulkInsertWords(words ?? []);
      game.latestWord = words?.slice(-1)[0];
      await game.bulkInsertTurns(turns ?? []);
    }
    return game;
  }

  // `restore` replays words from the server: keep their timestamp (if any)
  // rather than stamping them with the restore time
  async appendWord(word: Match, { restore = false } = {}) {
    if (!this.db) throw Error('not initialized');
    // if loading from BE, don't override swaps
    if (!word.swaps) {
      word.swaps = this.turns - this.prevTurnPointer;
      this.prevTurnPointer = this.turns;
    }
    // TODO store turn here
    await this.db.put(Game.wordKey, {
      ...word,
      index: this.words,
      timestamp: restore ? word.timestamp : (word.timestamp ?? Date.now()),
    });
    this.words++;
  }

  async appendTurn(coords: Coord[], duration?: number) {
    if (!this.db) throw Error('not initialized');
    const now = Date.now();
    await this.db.put(Game.turnKey, {
      coords,
      durationSeconds: duration ?? (now - this.lastTurnTimestamp) / 1000,
      words: range(this.prevWordPointer, this.words),
      index: this.turns,
    } as Turn);
    this.turns++;
    this.prevWordPointer = this.words;
    this.lastTurnTimestamp = now;
  }

  private chunk<T>(arr: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
    return result;
  }

  async bulkInsertWords(words: Match[], onProgress?: (count: number) => void, chunkSize = 50) {
    if (!this.db) throw Error('not initialized');
    for (const chunk of this.chunk(words, chunkSize)) {
      const tx = this.db.transaction(Game.wordKey, 'readwrite');
      for (const word of chunk) {
        tx.store.put({ ...word, index: this.words++ });
      }
      await tx.done;
      onProgress?.(this.words);
    }
    this.words = words.length;
  }

  async bulkInsertTurns(turns: Turn[], onProgress?: (count: number) => void, chunkSize = 50) {
    if (!this.db) throw Error('not initialized');
    const valid = turns.filter(
      (t) => Array.isArray(t?.coords) && t.coords.length > 0 && Array.isArray(t?.words)
    );
    for (const chunk of this.chunk(valid, chunkSize)) {
      const tx = this.db.transaction(Game.turnKey, 'readwrite');
      for (const turn of chunk) {
        tx.store.put({
          coords: turn.coords,
          durationSeconds: turn.durationSeconds ?? 0,
          words: turn.words,
          index: this.turns++,
        });
      }
      await tx.done;
      onProgress?.(this.turns);
    }
    this.lastTurnTimestamp = Date.now();
    this.turns = valid.length;
  }

  async getPage(sort: WordSort, offset: number, pageSize = 10) {
    if (!this.db) throw Error('not initialized');
    let tx = this.db.transaction(Game.wordKey);
    let cx = await tx.store.index(sort).openCursor(null, 'prev');

    if (cx && offset) {
      cx = await cx.advance(offset);
    }
    const page: Match[] = [];
    while (cx && page.length < pageSize) {
      page.push(cx.value);
      cx = await cx.continue();
    }
    await tx.done;
    return page;
  }

  async getAll(index: WordSort = 'index', fromIndex?: number): Promise<Match[]> {
    if (!this.db) throw Error('not initialized');
    const range = fromIndex != null ? IDBKeyRange.lowerBound(fromIndex) : undefined;
    return this.db.getAllFromIndex(Game.wordKey, index, range);
  }

  async getTurns(fromIndex?: number): Promise<Turn[]> {
    if (!this.db) throw Error('not initialized');
    const range = fromIndex != null ? IDBKeyRange.lowerBound(fromIndex) : undefined;
    return this.db.getAll(Game.turnKey, range);
  }

  async clear() {
    if (!this.db) throw Error('not initialized');
    await this.db.clear(Game.wordKey);
    await this.db.clear(Game.turnKey);
    await this.db.clear(Game.gameKey);
    this.turns = 0;
    this.words = 0;
    // TODO?
    // this.db.close();
  }

  swap([i, j]: Coord, [i2, j2]: Coord) {
    const first = this.board[i][j];
    const second = this.board[i2][j2];
    if (first && second) {
      this.board[i][j] = second;
      this.board[i2][j2] = first;
    }
    this.decrementSwaps([i, j], [i2, j2]);
  };

  decrementSwaps(c1: Coord, c2: Coord) { }

  getSwapPenalty(c1: Coord, c2: Coord) { return 0 }

  removeLetters(word: Match) {

    const filteredCoords = word.coords.filter(
      (c, i) => !this.intersections[word.word[i].id],
    );

    // clear column
    for (const [x, y] of filteredCoords) {
      delete this.board[x][y];
    }

    // clear intersections for cleared word
    for (const id of word.intersectingIds) {
      delete this.intersections[id];
    }
  }

  saveStats() {
    throw Error('not implemented');
  }
}

export const getWordsKey = (sort?: WordSort) => sort ? ['words', sort] : ['words'];

type WordsQueryArgs = { game: Game<Tile>; sort: WordSort; enabled: boolean };

// Must be called at component init (not inside $derived) so one observer
// follows option changes. The key includes the game and its word count, so a
// new word or a different game starts from a fresh first page instead of
// appending offset-based pages onto stale data.
export const wordsQuery = (args: () => WordsQueryArgs, pageSize = 20) =>
  createInfiniteQuery<Match[], unknown, { pages: Match[][] }, (string | number)[], number>(
    () => {
      const { game, sort, enabled } = args();
      return {
        queryKey: [...getWordsKey(sort), game?.id, game?.words],
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
          if (!game) throw Error('not initialized');
          return game.getPage(sort, pageParam, pageSize);
        },
        getNextPageParam: (lastPage, _pages, page) => {
          if (lastPage.length < pageSize) return undefined;
          return page + pageSize;
        },
        enabled: !!game && enabled,
        // local IndexedDB reads: evict once unobserved so the persister never
        // restores pages from an old board
        gcTime: 0,
      };
    },
  );
